import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { apiUrl } from './config/apiBase';
import { supabase } from './config/supabaseClient';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(() => {
    // Try to load from sessionStorage on initial mount
    try {
      const cached = sessionStorage.getItem('subscription_data');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [usage, setUsage] = useState(() => {
    // Try to load from sessionStorage on initial mount
    try {
      const cached = sessionStorage.getItem('usage_data');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [resumeUsage, setResumeUsage] = useState(() => {
    // Try to load from sessionStorage on initial mount
    try {
      const cached = sessionStorage.getItem('resume_usage_data');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const checkingRef = useRef(false);

  // Usage limits
  const LIMITS = {
    FREE_TRIAL: 3,      // 3 questions for free trial
    PAID_MONTHLY: 50,    // 50 questions per month for paid users
    FREE_RESUME_SCRATCH: 1,  // 1 resume from scratch for free users
    FREE_RESUME_TAILORED: 2,  // 2 job-tailored resumes for free users
    PAID_RESUME_SCRATCH_UNLIMITED: 999,  // Unlimited from-scratch resumes for paid users
    PAID_RESUME_TAILORED: 50  // 50 job-tailored resumes per month for paid users
  };

  // Check resume usage for current month
  const checkResumeUsage = useCallback(async () => {
    if (!user || checkingRef.current) return;

    checkingRef.current = true;

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        setResumeUsage({ scratchUsed: 0, tailoredUsed: 0, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
        return;
      }

      const token = sessionData?.session?.access_token;
      
      if (!token) {
        setResumeUsage({ scratchUsed: 0, tailoredUsed: 0, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
        return;
      }

      const response = await fetch(apiUrl('/api/resume-builder/usage'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const resumeUsageData = { 
          scratchUsed: data.scratchUsed || 0, 
          tailoredUsed: data.tailoredUsed || 0,
          month: data.month || new Date().getMonth() + 1, 
          year: data.year || new Date().getFullYear(),
          isPaid: data.isPaid || false
        };
        setResumeUsage(resumeUsageData);
        sessionStorage.setItem('resume_usage_data', JSON.stringify(resumeUsageData));
      } else {
        const defaultUsage = { 
          scratchUsed: 0, 
          tailoredUsed: 0,
          month: new Date().getMonth() + 1, 
          year: new Date().getFullYear(),
          isPaid: false
        };
        setResumeUsage(defaultUsage);
        sessionStorage.setItem('resume_usage_data', JSON.stringify(defaultUsage));
      }
    } catch (err) {
      const defaultUsage = { 
        scratchUsed: 0, 
        tailoredUsed: 0,
        month: new Date().getMonth() + 1, 
        year: new Date().getFullYear(),
        isPaid: false
      };
      setResumeUsage(defaultUsage);
      sessionStorage.setItem('resume_usage_data', JSON.stringify(defaultUsage));
    }

    checkingRef.current = false;
  }, [user]);
  const checkUsage = useCallback(async () => {
    if (!user || checkingRef.current) return;

    checkingRef.current = true;

    try {
      
      // Get the current session to get the access token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setUsage({ questionsUsed: 0, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
        return;
      }

      const token = sessionData?.session?.access_token;
      
      if (!token) {
        console.error('No access token available');
        setUsage({ questionsUsed: 0, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
        return;
      }

      const response = await fetch(apiUrl('/api/interview-questions/usage-simple'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const usageData = { 
          questionsUsed: data.used || 0, 
          month: data.month || new Date().getMonth() + 1, 
          year: data.year || new Date().getFullYear(),
          limit: data.limit || LIMITS.FREE_TRIAL,
          remaining: data.remaining || LIMITS.FREE_TRIAL,
          isPaid: data.isPaid || false
        };
        setUsage(usageData);
        sessionStorage.setItem('usage_data', JSON.stringify(usageData));
      } else {
        console.warn('Usage check failed:', response.status);
        // If no usage data found, assume 0 usage
        const defaultUsage = { 
          questionsUsed: 0, 
          month: new Date().getMonth() + 1, 
          year: new Date().getFullYear(),
          limit: LIMITS.FREE_TRIAL,
          remaining: LIMITS.FREE_TRIAL,
          isPaid: false
        };
        setUsage(defaultUsage);
        sessionStorage.setItem('usage_data', JSON.stringify(defaultUsage));
      }
    } catch (err) {
      console.error('Error checking usage:', err);
      const defaultUsage = { 
        questionsUsed: 0, 
        month: new Date().getMonth() + 1, 
        year: new Date().getFullYear(),
        limit: LIMITS.FREE_TRIAL,
        remaining: LIMITS.FREE_TRIAL,
        isPaid: false
      };
      setUsage(defaultUsage);
      sessionStorage.setItem('usage_data', JSON.stringify(defaultUsage));
    }

    checkingRef.current = false;
  }, [user, LIMITS.FREE_TRIAL]);
  const checkSubscription = useCallback(async () => {
    if (!user || checkingRef.current) return;

    checkingRef.current = true;

    try {
      setLoading(true);
      
      // Get the current session to get the access token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setSubscription(null);
        setLoading(false);
        return;
      }

      const token = sessionData?.session?.access_token;
      
      if (!token) {
        console.error('No access token available');
        setSubscription(null);
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl(`/api/stripe/subscriptions/${user.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 404 || response.status === 401) {
          // No subscription found - user hasn't made any payments yet
          setSubscription(null);
          sessionStorage.setItem('subscription_data', JSON.stringify(null));
        } else {
          console.warn('Failed to fetch subscription:', response.status);
          setSubscription(null);
          sessionStorage.setItem('subscription_data', JSON.stringify(null));
        }
      } else {
        const data = await response.json();
        // Check if user has any active subscriptions
        const activeSubscriptions = data.subscriptions?.filter(sub => 
          sub.status === 'active' || sub.status === 'trialing'
        );
        
        if (activeSubscriptions && activeSubscriptions.length > 0) {
          const subData = activeSubscriptions[0];
          setSubscription(subData); // Use the first active subscription
          sessionStorage.setItem('subscription_data', JSON.stringify(subData));
        } else {
          setSubscription(null);
          sessionStorage.setItem('subscription_data', JSON.stringify(null));
        }
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
      setError(err.message);
      setSubscription(null);
    } finally {
      setLoading(false);
      checkingRef.current = false;
    }
  }, [user]);

  // Check subscription when user changes (only when user actually changes, not on every render)
  useEffect(() => {
    if (!user) {
      // Reset state when user logs out
      setSubscription(null);
      setUsage(null);
      setResumeUsage(null);
      setLoading(false);
      sessionStorage.removeItem('subscription_data');
      sessionStorage.removeItem('usage_data');
      sessionStorage.removeItem('resume_usage_data');
      return;
    }
    
    // Clear cached data for new user
    sessionStorage.removeItem('subscription_data');
    sessionStorage.removeItem('usage_data');
    sessionStorage.removeItem('resume_usage_data');
    setSubscription(null);
    setUsage(null);
    setResumeUsage(null);
    
    // If we have cached data from sessionStorage, use it immediately and mark as loaded
    const hasCachedData = subscription !== null || usage !== null;
    
    if (hasCachedData) {
      // We have cached data, mark as not loading and use it
      setLoading(false);
      // Still refresh in background to get latest data
      setTimeout(() => {
        checkSubscription();
        checkUsage();
        checkResumeUsage();
      }, 100);
    } else {
      // No cached data, need to fetch
      checkSubscription();
      checkUsage();
      checkResumeUsage();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions
  const hasActiveSubscription = () => {
    return subscription && (subscription.status === 'active' || subscription.status === 'trialing');
  };

  const isPaidUser = () => {
    return hasActiveSubscription();
  };

  const isFreeUser = () => {
    return user && !hasActiveSubscription();
  };

  const canGenerateQuestions = () => {
    if (!user) return false;
    
    if (isPaidUser()) {
      // Paid users get 50 questions per month
      return !usage || usage.questionsUsed < LIMITS.PAID_MONTHLY;
    } else {
      // Free users get 1 question total
      return !usage || usage.questionsUsed < LIMITS.FREE_TRIAL;
    }
  };

  const getRemainingQuestions = () => {
    if (!user) return 0;
    
    const currentUsage = usage?.questionsUsed || 0;
    
    if (isPaidUser()) {
      return Math.max(0, LIMITS.PAID_MONTHLY - currentUsage);
    } else {
      return Math.max(0, LIMITS.FREE_TRIAL - currentUsage);
    }
  };

  const getUsageInfo = async () => {
    if (!user) return null;
    
    try {
      // Always fetch fresh data from database
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        return {
          used: 0,
          limit: LIMITS.FREE_TRIAL,
          remaining: LIMITS.FREE_TRIAL,
          isPaid: false,
          isFree: true,
          canGenerate: true
        };
      }

      const token = sessionData?.session?.access_token;
      
      if (!token) {
        return {
          used: 0,
          limit: LIMITS.FREE_TRIAL,
          remaining: LIMITS.FREE_TRIAL,
          isPaid: false,
          isFree: true,
          canGenerate: true
        };
      }

      const response = await fetch(apiUrl('/api/interview-questions/usage-simple'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const currentUsage = data.used || 0;
        const limit = data.limit || (isPaidUser() ? LIMITS.PAID_MONTHLY : LIMITS.FREE_TRIAL);
        const remaining = Math.max(0, limit - currentUsage);
        
        return {
          used: currentUsage,
          limit,
          remaining,
          isPaid: data.isPaid || false,
          isFree: !data.isPaid,
          canGenerate: currentUsage < limit
        };
      } else {
        // Fallback to cached data if API fails
        const currentUsage = usage?.questionsUsed || 0;
        const limit = isPaidUser() ? LIMITS.PAID_MONTHLY : LIMITS.FREE_TRIAL;
        const remaining = getRemainingQuestions();
        
        return {
          used: currentUsage,
          limit,
          remaining,
          isPaid: isPaidUser(),
          isFree: isFreeUser(),
          canGenerate: canGenerateQuestions()
        };
      }
    } catch (err) {
      // Fallback to cached data if error occurs
      const currentUsage = usage?.questionsUsed || 0;
      const limit = isPaidUser() ? LIMITS.PAID_MONTHLY : LIMITS.FREE_TRIAL;
      const remaining = getRemainingQuestions();
      
      return {
        used: currentUsage,
        limit,
        remaining,
        isPaid: isPaidUser(),
        isFree: isFreeUser(),
        canGenerate: canGenerateQuestions()
      };
    }
  };

  // Resume builder helper functions
  const canCreateScratchResume = () => {
    if (!user) return false;
    
    if (isPaidUser()) {
      return true; // Unlimited for paid users
    } else {
      const scratchUsed = resumeUsage?.scratchUsed || 0;
      return scratchUsed < LIMITS.FREE_RESUME_SCRATCH;
    }
  };

  const canCreateTailoredResume = () => {
    if (!user) return false;
    
    if (isPaidUser()) {
      // Paid users have a limit of 50 tailored resumes per month
      const tailoredUsed = resumeUsage?.tailoredUsed || 0;
      return tailoredUsed < LIMITS.PAID_RESUME_TAILORED;
    } else {
      const tailoredUsed = resumeUsage?.tailoredUsed || 0;
      return tailoredUsed < LIMITS.FREE_RESUME_TAILORED;
    }
  };

  const getResumeUsageInfo = async () => {
    if (!user) return null;
    
    try {
      // Always fetch fresh data from database
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        return {
          scratchUsed: 0,
          tailoredUsed: 0,
          scratchLimit: LIMITS.FREE_RESUME_SCRATCH,
          tailoredLimit: LIMITS.FREE_RESUME_TAILORED,
          scratchRemaining: LIMITS.FREE_RESUME_SCRATCH,
          tailoredRemaining: LIMITS.FREE_RESUME_TAILORED,
          isPaid: false,
          canCreateScratch: true,
          canCreateTailored: true
        };
      }

      const token = sessionData?.session?.access_token;
      
      if (!token) {
        return {
          scratchUsed: 0,
          tailoredUsed: 0,
          scratchLimit: LIMITS.FREE_RESUME_SCRATCH,
          tailoredLimit: LIMITS.FREE_RESUME_TAILORED,
          scratchRemaining: LIMITS.FREE_RESUME_SCRATCH,
          tailoredRemaining: LIMITS.FREE_RESUME_TAILORED,
          isPaid: false,
          canCreateScratch: true,
          canCreateTailored: true
        };
      }

      const response = await fetch(apiUrl('/api/resume-builder/usage'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const scratchUsed = data.scratchUsed || 0;
        const tailoredUsed = data.tailoredUsed || 0;
        const isPaid = data.isPaid || false;
        
        if (isPaid) {
          return {
            scratchUsed,
            tailoredUsed,
            scratchLimit: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
            tailoredLimit: LIMITS.PAID_RESUME_TAILORED,
            scratchRemaining: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
            tailoredRemaining: Math.max(0, LIMITS.PAID_RESUME_TAILORED - tailoredUsed),
            isPaid: true,
            canCreateScratch: true, // Always true for paid users
            canCreateTailored: tailoredUsed < LIMITS.PAID_RESUME_TAILORED
          };
        } else {
          return {
            scratchUsed,
            tailoredUsed,
            scratchLimit: LIMITS.FREE_RESUME_SCRATCH,
            tailoredLimit: LIMITS.FREE_RESUME_TAILORED,
            scratchRemaining: Math.max(0, LIMITS.FREE_RESUME_SCRATCH - scratchUsed),
            tailoredRemaining: Math.max(0, LIMITS.FREE_RESUME_TAILORED - tailoredUsed),
            isPaid: false,
            canCreateScratch: scratchUsed < LIMITS.FREE_RESUME_SCRATCH,
            canCreateTailored: tailoredUsed < LIMITS.FREE_RESUME_TAILORED
          };
        }
      } else {
        // Fallback to cached data if API fails
        const scratchUsed = resumeUsage?.scratchUsed || 0;
        const tailoredUsed = resumeUsage?.tailoredUsed || 0;
        
        if (isPaidUser()) {
          return {
            scratchUsed,
            tailoredUsed,
            scratchLimit: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
            tailoredLimit: LIMITS.PAID_RESUME_TAILORED,
            scratchRemaining: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
            tailoredRemaining: Math.max(0, LIMITS.PAID_RESUME_TAILORED - tailoredUsed),
            isPaid: true,
            canCreateScratch: true,
            canCreateTailored: canCreateTailoredResume()
          };
        } else {
          return {
            scratchUsed,
            tailoredUsed,
            scratchLimit: LIMITS.FREE_RESUME_SCRATCH,
            tailoredLimit: LIMITS.FREE_RESUME_TAILORED,
            scratchRemaining: Math.max(0, LIMITS.FREE_RESUME_SCRATCH - scratchUsed),
            tailoredRemaining: Math.max(0, LIMITS.FREE_RESUME_TAILORED - tailoredUsed),
            isPaid: false,
            canCreateScratch: canCreateScratchResume(),
            canCreateTailored: canCreateTailoredResume()
          };
        }
      }
    } catch (err) {
      // Fallback to cached data if error occurs
      const scratchUsed = resumeUsage?.scratchUsed || 0;
      const tailoredUsed = resumeUsage?.tailoredUsed || 0;
      
      if (isPaidUser()) {
        return {
          scratchUsed,
          tailoredUsed,
          scratchLimit: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
          tailoredLimit: LIMITS.PAID_RESUME_TAILORED,
          scratchRemaining: LIMITS.PAID_RESUME_SCRATCH_UNLIMITED,
          tailoredRemaining: Math.max(0, LIMITS.PAID_RESUME_TAILORED - tailoredUsed),
          isPaid: true,
          canCreateScratch: true,
          canCreateTailored: canCreateTailoredResume()
        };
      } else {
        return {
          scratchUsed,
          tailoredUsed,
          scratchLimit: LIMITS.FREE_RESUME_SCRATCH,
          tailoredLimit: LIMITS.FREE_RESUME_TAILORED,
          scratchRemaining: Math.max(0, LIMITS.FREE_RESUME_SCRATCH - scratchUsed),
          tailoredRemaining: Math.max(0, LIMITS.FREE_RESUME_TAILORED - tailoredUsed),
          isPaid: false,
          canCreateScratch: canCreateScratchResume(),
          canCreateTailored: canCreateTailoredResume()
        };
      }
    }
  };

  const refreshUsage = async () => {
    await checkUsage();
    await checkResumeUsage();
  };

  const refreshSubscription = () => {
    checkSubscription();
    checkUsage();
    checkResumeUsage();
  };

  const value = {
    subscription,
    usage,
    resumeUsage,
    loading,
    error,
    hasActiveSubscription,
    isPaidUser,
    isFreeUser,
    canGenerateQuestions,
    getRemainingQuestions,
    getUsageInfo,
    canCreateScratchResume,
    canCreateTailoredResume,
    getResumeUsageInfo,
    refreshSubscription,
    refreshUsage,
    checkSubscription,
    checkUsage,
    checkResumeUsage,
    LIMITS
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}